/**
 * UranIA PA — Compliance Dashboard (admin, `/admin/conformita` — era
 * `/admin/compliance`, fusione A.4.4/A.4.5, "Riordino della mappa delle
 * rotte" §4: "i gate di no-go sono conformità, non una materia a sé").
 * `/admin/conformita/gate` (era `/admin/cutover-gates`) vive ora sotto
 * questa — vedi `admin/conformita/gate/index.tsx`. `/admin/compliance`
 * resta come redirect qui.
 *
 * Pagina che racconta lo stato dei 20 principi AgID/AI Act/L. 132/2025
 * implementati nella piattaforma:
 *
 *   - ✅ operativi: codice e test presenti, attivi in pipeline
 *   - 🟡 Fase 3 in roadmap: previsti ma deferred al cutover compliance
 *     (audit S3 Object Lock, PII redaction runtime, FRIA, ecc.)
 *   - ❌ non implementati: out of scope pilota Sicilia
 *
 * Boundary: pagina puramente descrittiva (no fetch backend). Quando
 * arriverà US-025 Fase 3 "ComplianceDashboard widget 20 principi AgID"
 * questa pagina diventera' la home della dashboard live.
 */

import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import type { JSX } from 'react';

import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../../lib/auth/auth-store.ts';
import { principalIsAnyAdmin } from '../../../lib/auth/rbac.ts';
import { Route as AmministrazioneSegmentRoute } from '../../_amministrazione.tsx';

type PrincipleStatus = 'operativo' | 'roadmap_fase3' | 'roadmap_fase5';

interface Principle {
  readonly id: string;
  readonly category: string;
  readonly status: PrincipleStatus;
  readonly evidence: string;
}

const PRINCIPLES: readonly Principle[] = [
  // Trasparenza & disclosure
  { id: 'P.01', category: 'Trasparenza & disclosure', status: 'operativo',
    evidence: 'Banner AI Act art. 50 sticky su tutte le view + L. 132/2025 (AiDisclosureBanner).' },
  { id: 'P.02', category: 'Trasparenza & disclosure', status: 'operativo',
    evidence: 'explain_l1 (utente) + explain_l2 (auditor) in ogni GatewayResponse.' },
  { id: 'P.03', category: 'Trasparenza & disclosure', status: 'operativo',
    evidence: 'Citazioni [FONTE: doc_id, sez] HARD enforcement (pipeline fase [9] validation + retry vincolato).' },
  { id: 'P.04', category: 'Trasparenza & disclosure', status: 'operativo',
    evidence: 'DisclosureBannerClicked + AiDisclosureBannerDismissed eventi audit per ogni click banner.' },

  // Robustezza & sicurezza
  { id: 'P.05', category: 'Robustezza & sicurezza', status: 'operativo',
    evidence: 'Step-up MFA RFC 9470 (acr=L3) per write admin (skill, tenant, corpus, kill-switch).' },
  { id: 'P.06', category: 'Robustezza & sicurezza', status: 'operativo',
    evidence: 'Kill switch global + tenant-level con audit dell\'operatore (BC kill_switch).' },
  { id: 'P.07', category: 'Robustezza & sicurezza', status: 'operativo',
    evidence: 'Skill prompts cifrati AES-256-GCM (envelope encryption) con KEK Vault Transit per tenant.' },
  { id: 'P.08', category: 'Robustezza & sicurezza', status: 'roadmap_fase3',
    evidence: 'OWASP LLM Top 10 mapping (Fase 3 US-016) + guardrails input ON-by-default (Fase 3 US-012). Codice non implementato.' },

  // Multi-tenancy & isolamento
  { id: 'P.09', category: 'Multi-tenancy & isolamento', status: 'operativo',
    evidence: 'Postgres RLS + tenant_id ULID NOT NULL su ogni tabella business. Test isolation tenant_sicilia vs tenant_pilota_2.' },
  { id: 'P.10', category: 'Multi-tenancy & isolamento', status: 'operativo',
    evidence: 'Idempotency-Key required su Gateway invocations (replay 24h, draft-ietf-httpapi-idempotency-key).' },
  { id: 'P.11', category: 'Multi-tenancy & isolamento', status: 'operativo',
    evidence: 'Tenant context propagato via Principal JWT + set_tenant_context Postgres RLS.' },
  { id: 'P.12', category: 'Multi-tenancy & isolamento', status: 'operativo',
    evidence: 'Tenant scope esplicito su skill (core globale vs tenant-scoped) e su corpus (cross-tenant vs tenant-only).' },

  // Audit & accountability
  { id: 'P.13', category: 'Audit & accountability', status: 'operativo',
    evidence: 'audit_events Postgres partizionata + AuditWriter hash chain (prev_hash ⇄ self_hash, append-only).' },
  { id: 'P.14', category: 'Audit & accountability', status: 'operativo',
    evidence: 'agent_calls.jsonl WORM filesystem (dev). Ogni invocazione Gateway logga ts/agent_id/model/sources_used.' },
  { id: 'P.15', category: 'Audit & accountability', status: 'roadmap_fase3',
    evidence: 'S3 Object Lock 10y retention cross-region (Fase 3 US-001) + audit replicator worker (Fase 3 US-002).' },
  { id: 'P.16', category: 'Audit & accountability', status: 'roadmap_fase3',
    evidence: 'SPA AuditLogViewer per ruolo auditor (Fase 3 US-022). Backend chain integrity verifier CLI già operativo.' },

  // Diritti fondamentali & GDPR
  { id: 'P.17', category: 'Diritti fondamentali & GDPR', status: 'roadmap_fase3',
    evidence: 'PII redaction italiana (codice fiscale, IBAN, partita IVA) ON-by-default (Fase 3 US-012).' },
  { id: 'P.18', category: 'Diritti fondamentali & GDPR', status: 'roadmap_fase3',
    evidence: 'FRIA — Fundamental Rights Impact Assessment template + compilazione tenant Sicilia (Fase 3 US-010).' },
  { id: 'P.19', category: 'Diritti fondamentali & GDPR', status: 'roadmap_fase3',
    evidence: 'Retention policy automatica: delete PII 24m / drafts 12m / audit 10y soft-delete (Fase 3 US-023).' },
  { id: 'P.20', category: 'Diritti fondamentali & GDPR', status: 'roadmap_fase5',
    evidence: 'Bug bounty interno aperto a dipendenti PA tenant pilota (Fase 5 US-025). Non aperto in pilota Sicilia.' },
];

function StatusBadge(props: { readonly status: PrincipleStatus }): JSX.Element {
  const { t } = useTranslation();
  if (props.status === 'operativo') {
    return (
      <span
        className="badge"
        style={{ backgroundColor: '#198754', color: '#fff', fontSize: '0.65rem' }}
      >
        ✓ {t('admin.compliance.badge.operativo')}
      </span>
    );
  }
  if (props.status === 'roadmap_fase3') {
    return (
      <span
        className="badge"
        style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          border: '1px solid #ffeaa7',
          fontSize: '0.65rem',
        }}
      >
        ⏳ {t('admin.compliance.badge.fase3')}
      </span>
    );
  }
  return (
    <span
      className="badge"
      style={{
        backgroundColor: '#e7e7e7',
        color: '#495057',
        fontSize: '0.65rem',
      }}
    >
      ⏳ {t('admin.compliance.badge.fase5')}
    </span>
  );
}

export function AdminCompliancePage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isAdmin = principalIsAnyAdmin(principal);

  if (!isAuthenticated) {
    return (
      <section data-testid="admin-compliance-unauthenticated">
        <h1 className="h3 mb-3">{t('admin.compliance.title')}</h1>
        <div className="alert alert-info">
          <p className="mb-2">{t('admin.skills.unauthenticated.message')}</p>
          <Link to="/accedi" className="btn btn-primary">
            {t('admin.skills.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }
  if (!isAdmin) {
    return (
      <section data-testid="admin-compliance-forbidden">
        <h1 className="h3 mb-3">{t('admin.compliance.title')}</h1>
        <div className="alert alert-warning">
          <strong>{t('admin.skills.forbidden.title')}</strong>
          <p className="mb-0">{t('admin.skills.forbidden.body')}</p>
        </div>
      </section>
    );
  }

  // Group per categoria
  const categories = Array.from(new Set(PRINCIPLES.map((p) => p.category)));
  const operativi = PRINCIPLES.filter((p) => p.status === 'operativo').length;
  const fase3 = PRINCIPLES.filter((p) => p.status === 'roadmap_fase3').length;
  const fase5 = PRINCIPLES.filter((p) => p.status === 'roadmap_fase5').length;

  return (
    <section className="urania-admin-compliance" data-testid="admin-compliance-page">
      <nav className="mb-3">
        <Link to="/admin" className="small text-decoration-none">
          {t('admin.corpus.back_to_console')}
        </Link>
      </nav>

      {/* Hero blu */}
      <div
        className="rounded p-4 mb-4 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div className="position-relative" style={{ zIndex: 1, maxWidth: 760 }}>
          <span
            className="badge mb-2"
            style={{
              backgroundColor: 'rgba(255,255,255,0.18)',
              color: '#fff',
              fontSize: '0.7rem',
              letterSpacing: '0.05em',
            }}
          >
            ADMIN · COMPLIANCE DASHBOARD
          </span>
          <h1 className="h3 mb-2">{t('admin.compliance.title')}</h1>
          <p className="mb-0 opacity-90 small">{t('admin.compliance.subtitle')}</p>
        </div>
        <svg
          aria-hidden="true"
          className="position-absolute d-none d-lg-block"
          style={{ top: '-30px', right: '-30px', opacity: 0.12 }}
          width="200"
          height="200"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M5.338 1.59a61 61 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.7 10.7 0 0 0 2.287 2.233c.346.244.652.42.893.533q.18.085.293.118a1 1 0 0 0 .101.025 1 1 0 0 0 .1-.025q.114-.034.294-.118c.24-.113.547-.29.893-.533a10.7 10.7 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.8 11.8 0 0 1-2.517 2.453 7 7 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7 7 0 0 1-1.048-.625 11.8 11.8 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 63 63 0 0 1 5.072.56z" />
        </svg>
      </div>

      {/* Riepilogo */}
      <div className="row g-3 mb-4">
        <div className="col-sm-4">
          <div className="card shadow-sm border-0 h-100 text-center">
            <div className="card-body py-3">
              <div className="display-6 fw-bold" style={{ color: '#198754' }}>
                {operativi}
              </div>
              <p className="small text-muted mb-0">
                {t('admin.compliance.summary.operativi')}
              </p>
            </div>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="card shadow-sm border-0 h-100 text-center">
            <div className="card-body py-3">
              <div className="display-6 fw-bold" style={{ color: '#fd7e14' }}>
                {fase3}
              </div>
              <p className="small text-muted mb-0">
                {t('admin.compliance.summary.fase3')}
              </p>
            </div>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="card shadow-sm border-0 h-100 text-center">
            <div className="card-body py-3">
              <div className="display-6 fw-bold text-muted">{fase5}</div>
              <p className="small text-muted mb-0">
                {t('admin.compliance.summary.fase5')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Principi per categoria */}
      {categories.map((cat) => (
        <div key={cat} className="card shadow-sm border-0 mb-3">
          <div className="card-header bg-white border-bottom py-2 px-3">
            <span
              className="text-uppercase text-muted fw-semibold"
              style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
            >
              {cat}
            </span>
          </div>
          <div className="list-group list-group-flush">
            {PRINCIPLES.filter((p) => p.category === cat).map((p) => (
              <div
                key={p.id}
                className="list-group-item py-3"
                data-testid={`admin-compliance-principle-${p.id}`}
              >
                <div className="d-flex justify-content-between align-items-start gap-2 mb-1 flex-wrap">
                  <strong className="font-monospace small" style={{ color: '#06c' }}>
                    {p.id}
                  </strong>
                  <StatusBadge status={p.status} />
                </div>
                <p className="small text-muted mb-0 lh-sm">{p.evidence}</p>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="alert alert-info small mt-4 mb-0">
        <strong>{t('admin.compliance.note.title')}</strong>
        <p className="mb-0">{t('admin.compliance.note.body')}</p>
      </div>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => AmministrazioneSegmentRoute,
  path: '/admin/conformita',
  component: AdminCompliancePage,
});
